<template>
  <v-card outlined class="receipt-card receipt-table-card">
    <v-card-title>
      <v-spacer />
      <v-btn
        depressed
        color="primary"
        class="text-none font-weight-medium"
        :disabled="!hasPendingGroceries"
        @click="fillRemainingQuantities"
      >
        <v-icon left small>mdi-check-circle-outline</v-icon>
        {{ $t("warehouse.all_ordered_delivered") }}
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-data-table
        :headers="tableHeaders"
        :items="filteredGroceries"
        item-key="id"
        hide-default-footer
        class="receipt-table"
        :item-class="getReceiptRowClass"
      >
        <template v-slot:item.status="{ item }">
          <div class="d-inline-flex align-center">
            <v-icon small :color="isFullyDelivered(item) ? '#43a047' : '#fb8c00'">
              {{ isFullyDelivered(item) ? "mdi-check" : "mdi-circle-slice-8" }}
            </v-icon>
          </div>
        </template>

        <template v-slot:item.code="{ item }">
          {{ item.code || "-" }}
        </template>

        <template v-slot:item.supplier_code="{ item }">
          <text-field
            v-model="item.supplier_code"
            hide-details
            dense
          />
        </template>

        <template v-slot:item.name="{ item }">
          <span class="font-weight-medium">{{ item.name || "-" }}</span>
        </template>

        <template v-slot:item.measure="{ item }">
          {{ item.measure || "-" }}
        </template>

        <template v-slot:item.amount="{ item }">
          <div class="text-right">{{ formatPlainQuantity(item.amount) }}</div>
        </template>

        <template v-slot:item.delivered="{ item }">
          <div class="text-right">
            {{ formatPlainQuantity(getGroceryDelivered(item)) }}
          </div>
        </template>

        <template v-slot:item.order="{ item }">
          <div class="d-flex justify-end">
            <text-field
              v-if="!isFullyDelivered(item)"
              v-model="item.order"
              type="number"
              :min="0"
              :max="getRemaining(item)"
              hide-details
              dense
              class="receipt-amount-input"
              @input="normalizeQuantityInput(item)"
              @blur="normalizeQuantityInput(item)"
            />
            <v-icon v-else color="green darken-1">mdi-check-circle</v-icon>
          </div>
        </template>

        <template v-slot:item.gross="{ item }">
          <div class="text-right">
            {{ formatOptionalQuantity(getCurrentGrossQuantity(item)) }}
          </div>
        </template>

        <template v-slot:item.value="{ item }">
          <div class="text-right font-weight-bold">
            {{ formatOptionalMoney(getCurrentLineValue(item)) }}
          </div>
        </template>

        <template v-slot:item.unit_price="{ item }">
          <div class="d-flex justify-end">
            <text-field
              v-if="documentType === 'request'"
              :value="Number(item.unit_price) || ''"
              type="number"
              :min="0"
              hide-details
              dense
              class="receipt-amount-input"
              @input="$set(item, 'unit_price', Number($event) || null)"
            />
            <span v-else>{{ formatOptionalMoney(getCurrentUnitPrice(item)) }}</span>
          </div>
        </template>

        <template v-slot:body.append>
          <tr class="receipt-table-total-row">
            <td colspan="4"></td>
            <td class="text-right font-weight-medium">{{ $t("total") }}</td>
            <td class="text-right">{{ formatPlainQuantity(totalOrderedQuantity) }}</td>
            <td class="text-right">{{ formatOptionalQuantity(totalGrossQuantity) }}</td>
            <td class="text-right">
              {{ formatPlainQuantity(totalPreviouslyDeliveredQuantity) }}
            </td>
            <td class="text-right">{{ formatPlainQuantity(totalEnteredQuantity) }}</td>
            <td></td>
            <td class="text-right font-weight-bold">
              {{ formatOptionalMoney(totalReceiptValue) }}
            </td>
          </tr>
        </template>
      </v-data-table>

      <v-row class="pt-4" dense>
        <v-col cols="12" md="6">
          <div class="d-flex align-center body-1">
            <span class="mr-2">{{ $t("warehouse.total_quantity") }}:</span>
            <strong>{{ formatQuantity(totalEnteredQuantity, summaryMeasureLabel) }}</strong>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="d-flex align-center justify-md-end body-1">
            <span class="mr-2">{{ $t("warehouse.total_amount") }}:</span>
            <strong>{{ formatMoney(totalReceiptValue) }}</strong>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import hasReceiptCalculations from "@/mixins/warehouse/hasReceiptCalculations";

export default {
  name: "WarehouseReceiptItemsTable",
  mixins: [hasReceiptCalculations],
  props: {
    groceries: { type: Array, default: () => [] },
    documentTransactions: { type: Array, default: () => [] },
    documentType: { type: String, default: "" },
  },
  data() {
    return { search: "" };
  },
  computed: {
    hasPendingGroceries() {
      return this.groceries.some((g) => this.getRemaining(g) > 0);
    },
    filteredGroceries() {
      const q = this.translate((this.search || "").toLowerCase());
      return [...this.groceries]
        .filter(
          (item) =>
            !q ||
            this.translate((item.name || "").toLowerCase()).includes(q) ||
            String(item.code || "").toLowerCase().includes(q)
        )
        .sort((a, b) => {
          const aComplete = this.isFullyDelivered(a);
          const bComplete = this.isFullyDelivered(b);
          if (aComplete !== bComplete) return aComplete ? 1 : -1;
          const diff = this.getRemaining(b) - this.getRemaining(a);
          return diff !== 0 ? diff : (a.name || "").localeCompare(b.name || "");
        });
    },
    totalEnteredQuantity() {
      return this.groceries.reduce((sum, g) => sum + this.getCurrentReceiptAmount(g), 0);
    },
    totalOrderedQuantity() {
      return this.groceries.reduce((sum, g) => sum + (Number(g.amount) || 0), 0);
    },
    totalPreviouslyDeliveredQuantity() {
      return this.groceries.reduce((sum, g) => sum + this.getGroceryDelivered(g), 0);
    },
    totalGrossQuantity() {
      return this.groceries.reduce((sum, g) => sum + this.getCurrentGrossQuantity(g), 0);
    },
    totalReceiptValue() {
      return this.groceries.reduce((sum, g) => sum + this.getCurrentLineValue(g), 0);
    },
    summaryMeasureLabel() {
      const unique = [
        ...new Set(this.groceries.map((g) => g.measure).filter(Boolean)),
      ];
      return unique.length === 1 ? unique[0] : "";
    },
    tableHeaders() {
      return [
        { text: "", value: "status", sortable: false, align: "center", width: 56 },
        { text: this.$t("code"), value: "code", width: 90 },
        { text: this.$t("supplier_code"), value: "supplier_code", width: 110 },
        { text: this.$t("warehouse.product"), value: "name" },
        {
          text: this.$t("unit_of_measurment"),
          value: "measure",
          width: 270,
          align: "right",
        },
        {
          text: this.$t("warehouse.ordered"),
          value: "amount",
          align: "end",
          width: 110,
        },
        {
          text:
            this.$t("warehouse.ordered") +
            ` (${this.$t("warehouse.quantity_gross")})`,
          value: "gross",
          align: "end",
          sortable: false,
          width: 150,
        },
        {
          text: this.$t("previously_delivered"),
          value: "delivered",
          align: "end",
          sortable: false,
          width: 150,
        },
        {
          text: this.$t("warehouse.delivered_now"),
          value: "order",
          align: "end",
          sortable: false,
          width: 170,
        },
        {
          text: this.$t("price") + " " + this.restaurantCurrency,
          value: "unit_price",
          align: "end",
          sortable: false,
          width: 120,
        },
        {
          text: this.$t("amount") + " " + this.restaurantCurrency,
          value: "value",
          align: "end",
          sortable: false,
          width: 120,
        },
      ];
    },
  },
  methods: {
    fillRemainingQuantities() {
      this.groceries.forEach((grocery) => {
        if (!this.isFullyDelivered(grocery)) {
          this.$set(grocery, "order", this.getRemaining(grocery) || null);
          this.$set(
            grocery,
            "gross_quantity",
            Number(grocery.gross_quantity) || grocery.order || 0
          );
        }
      });
    },
    normalizeQuantityInput(item) {
      const value = Number(item.order);
      if (!Number.isFinite(value) || value <= 0) {
        item.order = value === 0 ? 0 : null;
        return;
      }
      item.order = Math.min(value, this.getRemaining(item));
    },
  },
};
</script>

<style scoped>
.receipt-card {
  border-radius: 4px;
  border-color: #e8e2db !important;
  background: #fff !important;
  box-shadow: none;
}

.receipt-table-card {
  overflow: hidden;
}

.receipt-table {
  border: 1px solid #eee6dd;
  border-radius: 4px;
  overflow: hidden;
}

.receipt-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.receipt-table ::v-deep table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.receipt-table ::v-deep thead th {
  background: #fff;
  color: #716559;
  font-size: 0.85rem;
  font-weight: 700;
  border-bottom: 1px solid #eee6dd !important;
  padding: 12px 10px;
}

.receipt-table ::v-deep tbody td {
  padding: 10px;
  border-bottom: 1px solid #f1ebe4 !important;
  font-size: 0.95rem;
  color: #342c25;
  background: #fff;
}

.receipt-table ::v-deep .receipt-table-row--completed td {
  background: #f5f8f2;
}

.receipt-amount-input {
  max-width: 96px;
}

.receipt-table-total-row td {
  color: #5b4f43;
}
</style>
